import React, { useState, useRef, useEffect, useCallback } from 'react';
import OT from '@opentok/client';

import './index.css';

import { styled } from '@mui/material/styles';
import { Fab } from '@mui/material';
import CallEndIcon from '@mui/icons-material/CallEnd';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';

import { getCookie } from '../../../../shared/helpers';

const TokboxVideoCall = ({
  apiKey,
  sessionId,
  token,
  amosTeladocId,
  onEndCall,
}) => {
  const [connection, setConnection] = useState('Connecting...');
  const [error, setError] = useState({
    code: null,
    message: '',
    name: '',
    stack: null,
  });
  const [publishVideo, setPublishVideo] = useState(true);
  const [publishAudio, setPublishAudio] = useState(true);

  const sessionRef = useRef(null);
  const publisherRef = useRef(null);
  const publisherElRef = useRef(null);
  const subscriberElRef = useRef(null);

  const tokboxApiKeyTest = getCookie('tokboxApiKeyTest');
  const tokboxSessionIdTest = getCookie('tokboxSessionIdTest');
  const tokboxTokenTest = getCookie('tokboxTokenTest');

  const effectiveApiKey = tokboxApiKeyTest || apiKey;
  const effectiveSessionId = tokboxSessionIdTest || sessionId;
  const effectiveToken = tokboxTokenTest || token;

  // Initialize session, publisher & subscribers
  useEffect(() => {
    const session = OT.initSession(effectiveApiKey, effectiveSessionId);
    sessionRef.current = session;

    // --- Session event handlers ---
    session.on('sessionConnected', () => {
      setConnection('Connected');
    });
    session.on('sessionDisconnected', () => {
      setConnection('Disconnected');
    });
    session.on('sessionReconnected', () => {
      setConnection('Reconnected');
    });
    session.on('sessionReconnecting', () => {
      setConnection('Reconnecting');
    });

    // Subscribe to incoming streams
    session.on('streamCreated', (event) => {
      session.subscribe(
        event.stream,
        subscriberElRef.current,
        { insertMode: 'append', width: '100%', height: '100%' },
        (err) => {
          if (err) {
            setError(err);
          } else {
            setConnection('Subscribe success');
          }
        }
      );
    });

    // --- Publisher ---
    const publisher = OT.initPublisher(
      publisherElRef.current,
      {
        insertMode: 'append',
        width: '100%',
        height: '100%',
        publishVideo: true,
        publishAudio: true,
      },
      (err) => {
        if (err) {
          setError(err);
        }
      }
    );
    publisherRef.current = publisher;

    publisher.on('accessDenied', () => {
      setConnection('User denied access to media source');
    });
    publisher.on('streamCreated', () => {
      setConnection('Publisher stream created');
    });
    publisher.on('streamDestroyed', ({ reason }) => {
      setConnection(`Publisher stream destroyed because: ${reason}`);
    });

    // Connect to the session
    session.connect(effectiveToken, (err) => {
      if (err) {
        setError(err);
        setConnection('Connect failed');
      } else {
        session.publish(publisher, (pubErr) => {
          if (pubErr) {
            setError(pubErr);
          } else {
            setConnection('Publish success');
          }
        });
      }
    });

    // Cleanup on unmount
    return () => {
      if (publisher) {
        publisher.destroy();
      }
      if (session) {
        session.disconnect();
      }
    };
  }, [effectiveApiKey, effectiveSessionId, effectiveToken]);

  // Toggle audio on the publisher
  useEffect(() => {
    if (publisherRef.current) {
      publisherRef.current.publishAudio(publishAudio);
    }
  }, [publishAudio]);

  // Toggle video on the publisher
  useEffect(() => {
    if (publisherRef.current) {
      publisherRef.current.publishVideo(publishVideo);
    }
  }, [publishVideo]);

  const toggleVideo = useCallback(() => {
    setPublishVideo((prev) => !prev);
  }, []);

  const toggleAudio = useCallback(() => {
    setPublishAudio((prev) => !prev);
  }, []);

  return (
    <>
      <TokboxContainer>
        <VideosContainer>
          {/* Subscriber (remote) video fills the container */}
          <div ref={subscriberElRef} className="OT_subscriber" />
          {/* Publisher (local) video as a small overlay */}
          <div ref={publisherElRef} className="OT_publisher" />
        </VideosContainer>
        <InvisibleLog>
          <p>
            <strong>Session Status: </strong>
            {connection}
          </p>
          {error && error.code ? (
            <p>
              <strong>{error.stack}</strong>
            </p>
          ) : null}
        </InvisibleLog>
        <ControlButtonsContainer>
          <ControlButton onClick={() => toggleAudio()}>
            {publishAudio ? <MicIcon /> : <MicOffIcon />}
          </ControlButton>
          <ControlButton onClick={() => onEndCall(amosTeladocId)}>
            <CallEndIcon />
          </ControlButton>
          <ControlButton onClick={() => toggleVideo()}>
            {publishVideo ? <VideocamIcon /> : <VideocamOffIcon />}
          </ControlButton>
        </ControlButtonsContainer>
      </TokboxContainer>
    </>
  );
};

const TokboxContainer = styled('div')({
  backgroundColor: '#d3d3d3',
  position: 'relative',
  zIndex: 1000000,
});

const VideosContainer = styled('div')({
  position: 'relative',
  width: '100%',
  minHeight: '90vh',
  backgroundColor: '#c3c3c3',
});

const ControlButtonsContainer = styled('div')({
  width: '216px',
  minHeight: '10vh',
  margin: '10px auto',
});

const ControlButton = styled(Fab)({
  margin: '8px',
});

const InvisibleLog = styled('div')({
  display: 'none',
  margin: '8px',
});

export default TokboxVideoCall;
